import {
  Typography,
  useTheme,
  useMediaQuery,
  Box,
  Stack,
  Link,
  Divider,
  Grid,
  Button,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AccordionSection from "../../shared/accordionSection";
import defaultImage from "@/img/default-image_210.png";
import dataService from "@/app/services/dataService";
import { useGlobalNotification } from "../../header/GlobalNotificationContext";

import CartToProductListSidebar from "@/app/pages/cart/cartToProductListSidebar";
import ProductListEnumType from "@/enums/productListEnumType";
import ItemSourceEnum from "@/enums/itemSourceEnum";
import { OrderItemModel } from "@/app/interfaces/response/OrderDetailsResponse";
import CommonHelper from "@/app/helpers/CommonHelper";
import React from "react";
import { useMutation } from "@tanstack/react-query";
import PageLoadingOverlay from "../../shared/pageLoadingOverlay";
import { CartItemRequest } from "@/app/models/product/types";
import { sendGAEvent } from "@/app/utils/googleAnalytics/ga4";
import CutSpecModel from "@/app/interfaces/CutSpecialModel";
import { LineItem } from "@/app/pages/product/productSpacificationLines";
import { isEagleViewProduct } from "@/app/models/constants";

const TITLE_PRODUCT_DETAILS = "Product Details";
const RE_ORDER_TEXT =
  "Product cannot be re-ordered online. Please contact branch to re-order.";

const ATTR_HEADERS = [
  "Item #",
  "Brand",
  "Color",
  "Measure",
  "Qty Ord",
  "Qty Available",
  "Unit Price",
  "UOM",
  "Unit Discount",
];

const HEADER_WIDTHS = [
  "110px",
  "110px",
  "110px",
  "110px",
  "110px",
  "110px",
  "110px",
  "110px",
  "110px",
];

const ProductDetails = ({
  productItems,
  orderId,
  isSAPOrder,
}: {
  productItems: OrderItemModel[];
  orderId: number;
  isSAPOrder: boolean;
}) => {
  const { setNotification } = useGlobalNotification();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [isApiLoading, setIsAPILoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [selectedOrderItem, setSelectedOrderItem] =
    useState<OrderItemModel | null>(null);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState<boolean>(false);
  const [selectedProductDescription, setSelectedProductDescription] = useState<
    string | null
  >(null);

  const addOrderItemToTemplate = useMutation({
    mutationFn: async ({
      productListId,
      type,
      orderItemId,
    }: {
      productListId: string;
      type?: ProductListEnumType;
      orderItemId?: number;
    }) => {
      const itemToUseId = orderItemId ?? selectedOrderItem?.id;

      if (type === ProductListEnumType.QuoteList) {
        return await dataService.AddProductToEstimate({
          estimateId: productListId,
          source: ItemSourceEnum.Order,
          orderItemId: itemToUseId,
          orderId: orderId,
        });
      }

      return await dataService.AddProductToTemplates({
        templateId: productListId,
        source: ItemSourceEnum.Order,
        orderItemId: itemToUseId,
        orderId: orderId,
      });
    },

    onSuccess: (response, { type }) => {
      const { success, data } = response || {};
      const {
        warnings = [],
        id,
        productListName: name,
        newProductListId,
        newProductListName,
        successMessage,
      } = data || {};

      if (success && warnings.length === 0) {
        const listTypeText =
          type === ProductListEnumType.QuoteList
            ? "Estimate List"
            : "Product Template";

        const url = `${process.env.REACT_APP_WEB_URL}/ProductListItem/List/${id}`;

        if (type === ProductListEnumType.BranchList) {
          const newUrl = `${process.env.REACT_APP_WEB_URL}/ProductListItem/List/${newProductListId}`;

          setNotification(
            <>
              Successfully added the product to the {listTypeText}{" "}
              <a href={newUrl}>{newProductListName}</a>.{" "}
              <a href={url}>{name}</a> has been archived
            </>,
            "success"
          );
          return;
        }

        setNotification(
          <>
            {successMessage.includes("{0}") ? (
              successMessage.split("{0}").map((part, index, arr) => (
                <React.Fragment key={index}>
                  {part}
                  {index < arr.length - 1 && <a href={url}>{name}</a>}
                </React.Fragment>
              ))
            ) : (
              <>
                {successMessage} <a href={url}>{name}</a>
              </>
            )}
          </>,
          "success"
        );
      } else {
        setNotification(warnings[0] ?? "Failed to add product.", "error");
      }
    },

    onError: () => {
      setNotification(
        "An unexpected error occurred while adding the product.",
        "error"
      );
    },

    onSettled: () => {
      setSidebarOpen(false);
    },
  });

  const handleAddToCart = async (orderItem: OrderItemModel) => {
    setIsAPILoading(true);

    const item: CartItemRequest = {
      productId: orderItem.productId,
      attributeValueId: orderItem.productAttributeValueId,
      quantity: Math.round(orderItem.quantity ?? 1),
      rib: orderItem.rib,
      box: orderItem.box,
      cartSpecificationLines: mapCutSpecsToLineItems(
        orderItem.orderItemSpecificationModelItems
      ),
    };
    try {
      const response = await dataService.addProductsToCart([item]);

      if (response.success) {
        const settingsRes = await dataService.getGASettings();
        const gaSettings = settingsRes.data;
        const addedItems = response.data.gaEvent?.items;

        sendGAEvent("add_to_cart", {
          currency: "USD",
          value: Number(orderItem.subTotal),
          items: (addedItems ?? []).map((item, index) => ({
            item_id: item.itemId,
            item_name: item.itemName,
            affiliation: gaSettings?.storeName ?? "LansingNOW",
            index: String(index),
            item_brand: (item.manufacturers ?? []).join(", "),
            item_category: item.categories?.[0] ?? "",
            item_category2: item.categories?.[1] ?? "",
            item_category3: item.categories?.[2] ?? "",
            item_category4: item.categories?.[3] ?? "",
            item_category5: item.categories?.[4] ?? "",
            item_list_id: "",
            item_list_name: "",
            item_variant: item.itemName,
            location_id: "",
            price: String(orderItem.unitPrice),
            quantity: String(orderItem.quantity),
            coupon: "",
            discount: "",
          })),
        });

        onAddToCartSuccess();
      } else {
        setNotification(<>{response.message || response.error}</>, "error");
      }
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setIsAPILoading(false);
    }
  };

  const onAddToCartSuccess = () => {
    window.dispatchEvent(new Event("refresh-cart"));
    setNotification(
      <>
        The product has been added to your <a href="/cart">Shopping Cart</a>.
      </>,
      "success"
    );
  };

  const mapCutSpecsToLineItems = (
    specs?: CutSpecModel[]
  ): LineItem[] | undefined => {
    if (!specs || specs.length === 0) return undefined;

    return specs.map((spec) => ({
      qty: spec.qty ?? "",
      lengthFt: spec.lengthFt ?? "",
      lengthIn: spec.lengthIn ?? "",
    }));
  };

  const addOrderItemToNewTemplate = useMutation({
    mutationFn: async ({
      name,
      description,
      type,
      orderItemId,
    }: {
      name?: string;
      description?: string;
      type?: ProductListEnumType;
      orderItemId?: number;
    }) => {
      if (!type || !name) throw new Error("Missing name or type");

      return await dataService.createNewEstimateOrTemplate(
        type,
        name.trim(),
        (description ?? "").trim()
      );
    },

    onSuccess: async (response, { type, name, orderItemId }) => {
      if (
        response.success &&
        (!response.data.errorMessage ||
          response.data.errorMessage.trim() === "")
      ) {
        await addOrderItemToTemplate.mutateAsync({
          productListId: response.data.id?.toString() ?? "0",
          type,
          orderItemId,
        });
      } else if (
        response.data.errorMessage &&
        response.data.errorMessage.trim() !== ""
      ) {
        setNotification(response.data.errorMessage, "error");
      }
    },

    onError: () => {
      setNotification(
        "An unexpected error occurred while adding the product.",
        "error"
      );
    },

    onSettled: () => {
      setSidebarOpen(false);
    },
  });
  function useCustomBreakpoint() {
    const [isCustomRange, setIsCustomRange] = useState(false);

    useEffect(() => {
      const mediaQuery = window.matchMedia(
        "(min-width: 0px) and (max-width: 1024px)"
      );
      setIsCustomRange(mediaQuery.matches);

      const listener = (e: {
        matches: boolean | ((prevState: boolean) => boolean);
      }) => setIsCustomRange(e.matches);
      mediaQuery.addListener(listener);
      return () => mediaQuery.removeListener(listener);
    }, []);

    return isCustomRange;
  }

  const isCustomRange = useCustomBreakpoint();
  const content = (
    <>
      <Box
        mt={1.5}
        p={isMobile ? 1 : 3}
        borderRadius={2}
        boxShadow={{
          xs: "0 0px 0px rgba(0,0,0,0)",
          md: "0 1px 3px rgba(0,0,0,0.1)",
        }}
        bgcolor="white"
        sx={{
          overflowX: { xs: "auto", md: "visible" },
          WebkitOverflowScrolling: "touch",
        }}
      >
        {!isMobile && (
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color="text.secondary"
            mb={2}
          >
            {TITLE_PRODUCT_DETAILS}
          </Typography>
        )}
        <Box
          sx={{
            pb: { xs: 0.5, md: 0 },
          }}
        >
          <Box
            sx={{
              minWidth: {
                md: "auto",
                overflowX: "auto",
              },
            }}
          >
            <Stack divider={<Divider />} spacing={2}>
              {productItems.map((product) => {
                const headersForThisProduct = product.brand
                  ? ATTR_HEADERS
                  : ATTR_HEADERS.filter((h) => h !== "Brand");

                const fullRow = [
                  CommonHelper.safeText(
                    isSAPOrder ? product.sapProduct : product.sku
                  ),
                  CommonHelper.safeText(product.brand),
                  CommonHelper.safeText(product.color),
                  CommonHelper.safeText(product.measure),
                  Math.round(product.quantity),
                  Math.round(product.quantityShipped),
                  CommonHelper.addDolarSign(product.unitPrice),
                  CommonHelper.safeText(product.uom),
                  CommonHelper.addDolarSign(product.discAmt),
                ];

                const rowForThisProduct = product.brand
                  ? fullRow
                  : fullRow.filter((_, idx) => idx !== 1);

                const rows = [rowForThisProduct];

                return (
                  <Box key={product.id} sx={{ py: 1, width: "100%" }}>
                    {isCustomRange ? (
                      <Box sx={{ width: "100%" }}>
                        <Grid container sx={{ width: "100%" }}>
                          <Grid
                            item
                            xs={12}
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              mb: 3,
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                              }}
                            >
                              <img
                                alt={product.productName ?? "No image"}
                                src={product.productPictureUrl ?? defaultImage}
                                loading="lazy"
                                style={{
                                  width: 200,
                                  height: 200,
                                  objectFit: "contain",
                                  backgroundColor:
                                    product.colorHex || "transparent",
                                }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="flex-start"
                              justifyContent="space-between"
                              sx={{ mb: 1 }}
                            >
                              <Stack spacing={0.5}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.75}
                                >
                                  {product.isAvailableInSelectedBranch ? (
                                    <Link
                                      className="request-link"
                                      fontSize="18px"
                                      fontWeight="normal"
                                      underline="none"
                                      fontStyle="italic"
                                      href={
                                        product.parentGroupedProductId
                                          ? `/lbpcustomer/product?id=${product.parentGroupedProductId}&associatedproduct=${product.productId}&uom=${product.productAttributeValueId}`
                                          : `/lbpcustomer/product?id=${product.productId}&uom=${product.productAttributeValueId}`
                                      }
                                      target="_blank"
                                    >
                                      {product.productName}
                                    </Link>
                                  ) : (
                                    <>
                                      <Typography
                                        fontSize="18px"
                                        fontWeight="normal"
                                        fontStyle="italic"
                                      >
                                        {product.productName}
                                      </Typography>
                                      <Tooltip title={RE_ORDER_TEXT}>
                                        <WarningAmberIcon
                                          sx={{
                                            color: "error.main",
                                            fontSize: 18,
                                          }}
                                          aria-label="Product cannot be re-ordered online"
                                        />
                                      </Tooltip>
                                    </>
                                  )}
                                </Stack>
                                {product.isSpecial && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "error.main",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Special Order
                                  </Typography>
                                )}
                              </Stack>

                              <Typography
                                color="error.main"
                                fontWeight={600}
                                whiteSpace="nowrap"
                                sx={{ pl: 1 }}
                              >
                                {CommonHelper.addDolarSign(product.subTotal)}
                              </Typography>
                            </Stack>
                            {product.fullDescription && (
                              <Box sx={{ mt: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setSelectedProductDescription(
                                      product.fullDescription ?? ""
                                    );
                                    setIsDescriptionOpen(true);
                                  }}
                                >
                                  FULL DESCRIPTION
                                </Button>
                              </Box>
                            )}
                            <Box>
                              {rows.map((row, rowIndex) => (
                                <Box
                                  className="verySmallDevices"
                                  key={rowIndex}
                                  sx={{ mb: 1.5 }}
                                >
                                  {headersForThisProduct.map(
                                    (header, index) =>
                                      index % 2 === 0 && (
                                        <Box
                                          key={index}
                                          sx={{
                                            display: "flex",
                                            gap: 2,
                                            mb: 0.75,
                                            fontSize: "14px",
                                          }}
                                        >
                                          <Box
                                            sx={{ flex: 1, display: "flex" }}
                                          >
                                            <Box
                                              sx={{
                                                mr: 0.5,
                                                fontWeight: 500,
                                                color: "#0009",
                                              }}
                                            >
                                              {headersForThisProduct[index]}:
                                            </Box>
                                            <Box
                                              sx={{
                                                flex: 1,
                                                fontWeight: 600,
                                                color: "#494440",
                                              }}
                                            >
                                              {row[index]}
                                            </Box>
                                          </Box>

                                          {headersForThisProduct[index + 1] && (
                                            <Box
                                              sx={{
                                                flex: 1,
                                                display: "flex",
                                                fontSize: "14px",
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  fontWeight: 500,
                                                  color: "#0009",
                                                  mr: 0.5,
                                                }}
                                              >
                                                {
                                                  headersForThisProduct[
                                                    index + 1
                                                  ]
                                                }
                                                :
                                              </Box>
                                              <Box
                                                sx={{
                                                  flex: 1,
                                                  fontWeight: 600,
                                                  color: "#494440",
                                                }}
                                              >
                                                {row[index + 1]}
                                              </Box>
                                            </Box>
                                          )}
                                        </Box>
                                      )
                                  )}
                                </Box>
                              ))}
                            </Box>
                            {product.box && product.rib && (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{
                                  mt: 0.25,
                                  ml: 0.5,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ display: "flex", gap: 1 }}
                                >
                                  <span style={{ fontWeight: 600 }}>Box</span>
                                  {` ${product.box ?? 0} `}
                                  <span style={{ fontWeight: 600 }}>RIB</span>
                                  {` ${product.rib ?? 0} `}
                                </Typography>
                              </Stack>
                            )}
                            {product.orderItemSpecificationModelItems &&
                              product.orderItemSpecificationModelItems.map(
                                (item, index) => (
                                  <Stack
                                    key={index}
                                    direction="row"
                                    spacing={0.5}
                                    sx={{
                                      mt: 0.25,
                                      ml: 0.5,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{ display: "flex", gap: 1 }}
                                    >
                                      <span style={{ fontWeight: 600 }}>
                                        Qty
                                      </span>
                                      {` ${item.qty ?? 0} `}
                                      <span style={{ fontWeight: 600 }}>
                                        *Length
                                      </span>
                                      {` ${item.lengthFt ?? 0} `}
                                      <span style={{ fontWeight: 600 }}>
                                        Ft
                                      </span>
                                      {` ${item.lengthIn ?? 0} `}
                                      <span style={{ fontWeight: 600 }}>
                                        In
                                      </span>
                                    </Typography>
                                  </Stack>
                                )
                              )}
                            {product.isAvailableInSelectedBranch && !isEagleViewProduct(product.sku) && (
                              <Stack
                                direction="column"
                                spacing={isMobile ? 1 : 2}
                                sx={{
                                  mt: 1.25,
                                  width: "100%",
                                  maxWidth: 520,
                                  ml: "-4px",
                                }}
                              >
                                <Button
                                  size="small"
                                  className="request-link"
                                  sx={{
                                    textTransform: "capitalize",
                                    fontSize: "14px",
                                    fontFamily:
                                      '"Montserrat", Verdana, sans-serif',
                                    whiteSpace: "nowrap",
                                  }}
                                  onClick={() => {
                                    handleAddToCart(product);
                                  }}
                                >
                                  Add to cart ({Math.round(product.quantity)})
                                </Button>
                                <Button
                                  size="small"
                                  style={{
                                    textTransform: "capitalize",
                                    fontSize: "14px",
                                    fontFamily:
                                      '"Montserrat", Verdana, sans-serif',
                                  }}
                                  onClick={() => {
                                    setSelectedOrderItem(product);
                                    setSidebarOpen(true);
                                  }}
                                  className="request-link"
                                >
                                  Add to Template/Estimate
                                </Button>
                              </Stack>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    ) : (
                      <>
                        <Grid
                          container
                          spacing={2}
                          alignItems="center"
                          wrap="nowrap"
                        >
                          <Grid
                            item
                            xs="auto"
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                px: "50px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <img
                                alt={product.productName ?? "No image"}
                                src={product.productPictureUrl ?? defaultImage}
                                loading="lazy"
                                style={{
                                  width: 100,
                                  height: 100,
                                  objectFit: "contain",
                                  backgroundColor:
                                    product.colorHex || "transparent",
                                }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs minWidth={0}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ mb: 1 }}
                            >
                              <Stack spacing={0.5}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.75}
                                >
                                  {product.isAvailableInSelectedBranch ? (
                                    <Link
                                      className="request-link"
                                      fontSize="18px"
                                      fontWeight="normal"
                                      underline="none"
                                      fontStyle="italic"
                                      href={
                                        product.parentGroupedProductId
                                          ? `/lbpcustomer/product?id=${product.parentGroupedProductId}&associatedproduct=${product.productId}&uom=${product.productAttributeValueId}`
                                          : `/lbpcustomer/product?id=${product.productId}&uom=${product.productAttributeValueId}`
                                      }
                                      target="_blank"
                                    >
                                      {product.productName}
                                    </Link>
                                  ) : (
                                    <>
                                      <Typography
                                        fontSize="18px"
                                        fontWeight="normal"
                                        fontStyle="italic"
                                      >
                                        {product.productName}
                                      </Typography>
                                      <Tooltip title={RE_ORDER_TEXT}>
                                        <WarningAmberIcon
                                          sx={{
                                            color: "error.main",
                                            fontSize: 18,
                                          }}
                                          aria-label="Product cannot be re-ordered online"
                                        />
                                      </Tooltip>
                                    </>
                                  )}
                                </Stack>
                                {product.isSpecial && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "error.main",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Special Order
                                  </Typography>
                                )}
                              </Stack>

                              <Typography
                                color="error.main"
                                fontWeight={600}
                                whiteSpace="nowrap"
                                sx={{ pl: 1 }}
                              >
                                {CommonHelper.addDolarSign(product.subTotal)}
                              </Typography>
                            </Stack>
                            {product.fullDescription && (
                              <Box sx={{ mt: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setSelectedProductDescription(
                                      product.fullDescription ?? ""
                                    );
                                    setIsDescriptionOpen(true);
                                  }}
                                >
                                  FULL DESCRIPTION
                                </Button>
                              </Box>
                            )}
                            <Box
                              className="tableNoBorder noPaddingFirst"
                              sx={{ overflowX: "inherit" }}
                            >
                              <TableContainer
                                sx={{ border: 0, overflowX: "visible" }}
                              >
                                <Table
                                  size="small"
                                  sx={{
                                    tableLayout: "fixed",
                                    width: "100%",
                                    borderCollapse: "collapse",
                                  }}
                                >
                                  <TableHead>
                                    <TableRow>
                                      {headersForThisProduct.map(
                                        (header, index) => (
                                          <TableCell
                                            key={index}
                                            sx={{
                                              width: HEADER_WIDTHS[index],
                                              minWidth: HEADER_WIDTHS[index],
                                              maxWidth: HEADER_WIDTHS[index],
                                              p: 0.75,
                                              m: 0,
                                              borderBottom: "1px solid #e0e0e0",
                                              fontWeight: 600,
                                              fontSize: "0.875rem",
                                              color: "text.secondary",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {header}
                                          </TableCell>
                                        )
                                      )}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {rows.map((row, rowIndex) => (
                                      <TableRow key={rowIndex}>
                                        {row.map((cell, cellIndex) => (
                                          <TableCell
                                            key={cellIndex}
                                            sx={{
                                              width: HEADER_WIDTHS[cellIndex],
                                              minWidth:
                                                HEADER_WIDTHS[cellIndex],
                                              maxWidth:
                                                HEADER_WIDTHS[cellIndex],
                                              p: 0.75,
                                              m: 0,
                                              borderBottom: "1px solid #f5f5f5",
                                              fontSize: "0.875rem",
                                            }}
                                          >
                                            {cell}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                            {product.box && product.rib && (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{
                                  mt: 0.25,
                                  ml: 0.5,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ display: "flex", gap: 1 }}
                                >
                                  <span style={{ fontWeight: 600 }}>Box</span>
                                  {` ${product.box ?? 0} `}
                                  <span style={{ fontWeight: 600 }}>RIB</span>
                                  {` ${product.rib ?? 0} `}
                                </Typography>
                              </Stack>
                            )}
                            {product.orderItemSpecificationModelItems &&
                              product.orderItemSpecificationModelItems.map(
                                (item, index) => (
                                  <Stack
                                    key={index}
                                    direction="row"
                                    spacing={0.5}
                                    sx={{
                                      mt: 0.25,
                                      ml: 0.5,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{ display: "flex", gap: 1 }}
                                    >
                                      <span style={{ fontWeight: 600 }}>
                                        Qty
                                      </span>
                                      {` ${item.qty ?? 0} `}
                                      <span style={{ fontWeight: 600 }}>
                                        *Length
                                      </span>
                                      {` ${item.lengthFt ?? 0} `}
                                      <span style={{ fontWeight: 600 }}>
                                        Ft
                                      </span>
                                      {` ${item.lengthIn ?? 0} `}
                                      <span style={{ fontWeight: 600 }}>
                                        In
                                      </span>
                                    </Typography>
                                  </Stack>
                                )
                              )}
                            {product.isAvailableInSelectedBranch && !isEagleViewProduct(product.sku) && (
                              <Stack
                                direction="row"
                                spacing={isMobile ? 1 : 2}
                                sx={{
                                  mt: 1.25,
                                  width: "100%",
                                  maxWidth: 520,
                                  ml: "-4px",
                                }}
                              >
                                <Button
                                  size="small"
                                  className="request-link"
                                  sx={{
                                    textTransform: "capitalize",
                                    fontSize: "14px",
                                    fontFamily:
                                      '"Montserrat", Verdana, sans-serif',
                                    whiteSpace: "nowrap",
                                  }}
                                  onClick={() => {
                                    handleAddToCart(product);
                                  }}
                                >
                                  Add to cart ({Math.round(product.quantity)})
                                </Button>
                                <Button
                                  size="small"
                                  style={{
                                    textTransform: "capitalize",
                                    fontSize: "14px",
                                    fontFamily:
                                      '"Montserrat", Verdana, sans-serif',
                                  }}
                                  onClick={() => {
                                    setSelectedOrderItem(product);
                                    setSidebarOpen(true);
                                  }}
                                  className="request-link"
                                >
                                  Add to Template/Estimate
                                </Button>
                              </Stack>
                            )}
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>
        <Dialog
          open={isDescriptionOpen}
          onClose={() => setIsDescriptionOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, textAlign: "center" }}>
            ITEM FULL DESCRIPTION
          </DialogTitle>
          <DialogContent sx={{ textAlign: "center", mt: 1 }}>
            <Typography>{selectedProductDescription}</Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setIsDescriptionOpen(false)}
            >
              CLOSE
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );

  return (
    <>
      {isMobile ? (
        <AccordionSection title={TITLE_PRODUCT_DETAILS}>
          {content}
        </AccordionSection>
      ) : (
        content
      )}
      {isApiLoading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1800,
            pointerEvents: "auto",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#1976d2" }} />
        </Box>
      )}
      <CartToProductListSidebar
        open={sidebarOpen}
        onCartToNewTemplate={(
          name?: string,
          description?: string,
          type?: ProductListEnumType
        ) => {
          if (name && type && selectedOrderItem) {
            addOrderItemToNewTemplate.mutate({
              name,
              description,
              type,
              orderItemId: selectedOrderItem.id,
            });
          }
        }}
        onCartToTemplate={(id?: string, type?: ProductListEnumType) => {
          if (id && type && selectedOrderItem) {
            addOrderItemToTemplate.mutate({
              productListId: id,
              type,
              orderItemId: selectedOrderItem.id,
            });
          }
        }}
        onClose={() => {
          setSelectedOrderItem(null);
          setSidebarOpen(false);
        }}
      />
      {(addOrderItemToTemplate.isPending ||
        addOrderItemToNewTemplate.isPending) && <PageLoadingOverlay />}
    </>
  );
};

export default ProductDetails;
